import { useState, useEffect, useMemo, useRef } from "react";
import {
  useToast, Text, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, Icon, Modal,
  ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, Box, Button, useDisclosure, Stack, FormControl, FormLabel, Select, Input,
  CloseButton, Switch,
  InputRightElement, InputGroup,
  Tooltip, HStack, Progress,
} from '@chakra-ui/react';
import { MdDelete, MdCheckCircle } from "react-icons/md";
import { FaDownload } from "react-icons/fa";
import { react_api } from "../../config";
import createAxiosInstance from "../../interceptors/useAxiosInterceptors";
import logout from "../../services/authService";
import { useSession } from "../../SessionContext";
import { HTTP_STATUS_CODES } from "../../GrinsCostant";
import { hasAccents } from "../../services/utils";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function UploadDatasetModal ({ isOpen, onClose }){
    const { t } = useTranslation();
    const axiosInstance = createAxiosInstance(async (axiosInstance) => {
        logout(t, clearSessionData, navigate, toast, axiosInstance)
    });
    const { updateSessionData, clearSessionData } = useSession();
    const navigate = useNavigate();
    const [spinnerLoading, setSpinnerLoading] = useState(false);
    const toast = useToast();
    const fileFormats = [{ label: "", value: [""] }, { label: "csv", value: [".csv"] }, { label: "xlsx", value: [".xlsx", ".xls"] }, { label: "shapefile", value: [".zip", ".rar"] }
    , { label: "json", value: [".json"] },
    { label: "GeoJSON", value: [".geojson"] },
    { label: "GeoPackage", value: [".gpkg"] }
    ];
    const fileSelectorRef = useRef(null);
    let defaultPayloadUploadRequestValue = { version: "1.0", titolo: null, file: null, fileFormat: "", is_update: false, updateDate: "" }
    const [payloadUploadRequest, setPayloadUploadRequest] = useState(defaultPayloadUploadRequestValue);
    const [selectedFileFormat, setSelectedFileFormat] = useState(fileFormats[0].value);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [maxFileSize, setMaxFileSize] = useState(Infinity);
    const [selectedMessage, setSelectedMessage] = useState('');
    const [isValidating, setIsValidating] = useState(false);
    const [currentUrl, setCurrentUrl] = useState("");
    const [isUploadUrl, setIsUploadUrl] = useState(null)
    const [isUpdate, setIsUpdate] = useState(false);
    const fileUrlFormats = [
    { label: "", value: [""] },
    { label: "csv", value: [".csv"] },
    { label: "xlsx", value: [".xlsx", ".xls"] }
    ];
    const fileFormatsOptions = useMemo(() => {
        return isUploadUrl ? fileUrlFormats : fileFormats;
    }, [isUploadUrl]); 

    const isValidUrlFormat = (url) => {
        try {
            new URL(url);
            return url.startsWith('http://') || url.startsWith('https://');
        } catch (error) {
            return false;
        }
    };
    const validateUrl = async () => {
        const url = currentUrl.trim();
        if (!url) {
            toast({
                title: t('toastMsg.uploadDatasetModal.urlIsMandatory'),
                position: 'top',
                status: 'error',
                duration: 4000,
                isClosable: true
            });
            return;
        }
        if (!isValidUrlFormat(url)) {
            toast({
                title: t('toastMsg.uploadDatasetModal.invalidFormatUrl'),
                position: 'top',
                status: 'error',
                duration: 4000,
                isClosable: true
            });
            return;
        }
        setIsValidating(true);

        try {
            const response = await fetch(url, { method: 'HEAD', mode: 'no-cors' })
                .then(() => ({ success: true }))
                .catch(() => ({ success: false }));

            if (response.success) {
                try {
                    const urlObj = new URL(url);
                    const pathname = urlObj.pathname;
                    const ext = pathname.substring(pathname.lastIndexOf('.')).toLowerCase();

                    const selectedFormat = payloadUploadRequest.fileFormat;
                    const allowedFormat = fileFormats.find(f => f.label.toLowerCase() === selectedFormat.toLowerCase());

                    if (allowedFormat && !allowedFormat.value.includes(ext)) {
                        toast({
                            title: t('toastMsg.error'),
                            description: t('toastMsg.uploadDatasetModal.fileMustBeInFormat') + " " + selectedFormat.toUpperCase() + " (" + t('toastMsg.uploadDatasetModal.extension') + " : " + allowedFormat.value.join(" / ") + ") ",
                            position: 'top',
                            status: 'error',
                            duration: 4000,
                            isClosable: true
                        });
                        setIsValidating(false);
                        return;
                    }
                } catch (err) {
                    toast({
                        title: t('toastMsg.error'),
                        description: t('toastMsg.uploadDatasetModal.errorDuringValidation'),
                        position: 'top',
                        status: 'error',
                        duration: 4000,
                        isClosable: true
                    });
                    setIsValidating(false);
                    return;
                }
                setPayloadUploadRequest(prev => ({
                    ...prev,
                    urlDownloadFile: url
                }));
                setCurrentUrl("");
                toast({
                    title: t('toastMsg.uploadDatasetModal.urlSuccessfullyValidated'),
                    status: 'success',
                    position: "top",
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: t('toastMsg.error'),
                    description: t('toastMsg.uploadDatasetModal.urlIsInvalid'),
                    position: 'top',
                    status: 'error',
                    duration: 4000,
                    isClosable: true
                });
            }
        } catch (error) {
            toast({
                title: t('toastMsg.error'),
                description: t('toastMsg.uploadDatasetModal.errorDuringValidation'),
                position: 'top',
                status: 'error',
                duration: 4000,
                isClosable: true
            });
        } finally {
            setIsValidating(false);
        }
    };
    const removeUrl = () => {
        setPayloadUploadRequest(prev => ({
            ...prev,
            urlDownloadFile: ""
        }));
        toast({
            title: t('toastMsg.uploadDatasetModal.urlRemoved'),
            status: 'info',
            position: "top",
            duration: 3000,
            isClosable: true,
        });
    };

    const uploadDataset = async () => {
        if (payloadUploadRequest.file && payloadUploadRequest.file.size > maxFileSize) {
            toast({
                title: t('toastMsg.error'),
                description: t('toastMsg.uploadDatasetModal.fileExceedsMaximumSize') + '(MAX : ' + (maxFileSize / (1024 * 1024)) + ' MB)',
                status: 'error',
                duration: 4000,
                isClosable: true
            });
            return;
        }
        if (payloadUploadRequest.is_update) {
            if (
                payloadUploadRequest.updateDate == null || payloadUploadRequest.updateDate.length === 0 ||
                (!isUploadUrl && payloadUploadRequest.file == null) ||
                (isUploadUrl && payloadUploadRequest.urlDownloadFile == null) ||
                payloadUploadRequest.fileFormat == null
            ) {
                toast({
                    title: t('toastMsg.error'),
                    description: isUploadUrl ? t('toastMsg.uploadDatasetModal.updateFileUrl') : t('toastMsg.uploadDatasetModal.updateFile'),
                    status: 'warning',
                    duration: 4000,
                    isClosable: true
                });
                return;
            }
        } else {
            if (
                payloadUploadRequest.titolo == null || payloadUploadRequest.titolo.length === 0 ||
                (!isUploadUrl && payloadUploadRequest.file == null) ||
                (isUploadUrl && payloadUploadRequest.urlDownloadFile == null) ||
                (payloadUploadRequest.fileFormat == null || payloadUploadRequest.fileFormat === "")
            ) {
                toast({
                    title: t('toastMsg.error'),
                    description: isUploadUrl ? t('toastMsg.uploadDatasetModal.newFileUrl') : t('toastMsg.uploadDatasetModal.newFile'),
                    status: 'warning',
                    duration: 4000,
                    isClosable: true
                });
                return;
            }
        }

        try {
            setIsUploading(true);
            setUploadProgress(0);

            const fileSize = payloadUploadRequest.file ? payloadUploadRequest.file.size : 0;
            const fileSizeMB = fileSize / (1024 * 1024);
            const factor = 0.2;
            const simulatedDurationSec = Math.max(fileSizeMB * factor, 5);
            const incrementPerSec = simulatedDurationSec > 0 ? 97 / simulatedDurationSec : 97;

            const progressTimer = setInterval(() => {
                setUploadProgress(prev => {
                    const next = prev + incrementPerSec;
                    if (next >= 97) {
                        clearInterval(progressTimer);
                        return 97;
                    }
                    return next;
                });
            }, 1000);

            const formData = new FormData();
            formData.append("username", sessionStorage.getItem("username"));

            Object.keys(payloadUploadRequest).forEach(payloadUploadRequestKey => {
                formData.append(payloadUploadRequestKey, payloadUploadRequest[payloadUploadRequestKey]);
            });

            const options = {
                headers: {
                    'Authorization': sessionStorage.getItem("token"),
                    'Content-Type': 'multipart/form-data',
                },
            };
            // const urlFetch = isUploadUrl ? `${react_api}/privateServices/uploadDataADPUrl` : `${react_api}/privateServices/uploadDataADP`;
            const urlFetch = `${react_api}/privateServices/uploadDataADP`;
            const response = await axiosInstance.post(urlFetch, formData, options);
            const result = response.data;

            if (result.status === HTTP_STATUS_CODES.OK) {
                setUploadProgress(100);
                toast({
                    title: t(`messageBE.${result.message}`),
                    description: "",
                    status: 'info',
                    duration: 4000,
                    isClosable: true
                });
                onClose();
                setSelectedFileFormat(fileFormats[0].value)
                setPayloadUploadRequest(defaultPayloadUploadRequestValue);
            } else if (result.status !== HTTP_STATUS_CODES.OK) {
                toast({
                    title: t(`messageBE.${result.message}`),
                    description: "",
                    status: 'warning',
                    duration: 4000,
                    isClosable: true
                });
            } else {
                throw new Error(t('navbar.throwNewError.dataRecoveryError'));
            }

        } catch (error) {
                toast({
                title: t('toastMsg.callError'),
                description: String(error),
                status: 'warning',
                duration: 4000,
                isClosable: true
                });
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    }

    const handleChangePayloadRequest = (event, key) => {
        switch (key) {
            case "version":
                setPayloadUploadRequest(payloadUploadRequest => ({
                    ...payloadUploadRequest,
                    [key]: event
                }));
            break
            case "titolo":
                setPayloadUploadRequest(payloadUploadRequest => ({
                    ...payloadUploadRequest,
                    [key]: event.target.value
                }));
            break
            case "fileFormat":
                switch (event.target.selectedOptions[0].label) {
                    case "csv":
                        toast({
                            title: t('toastMsg.navbar.info'),
                            description: t('toastMsg.navbar.csvDelimiters'),
                            status: 'info',
                            duration: 4000,
                            isClosable: true
                        })
                    break
                    case 'shapefile':
                        toast({
                            title: t('toastMsg.navbar.info'),
                            description: t('toastMsg.navbar.shapefileFormat'),
                            status: 'info',
                            duration: 4000,
                            isClosable: true
                        })
                }
                setSelectedFileFormat(event.target.value.split(","))
                let label = event.target.selectedOptions[0].label
                if (label == "GeoJSON")
                    label = "geojson"

                if (label == "GeoPackage")
                    label = "gpkg"
                setPayloadUploadRequest(payloadUploadRequest => ({
                    ...payloadUploadRequest,
                    [key]: label
                }));

                setPayloadUploadRequest(payloadUploadRequest => ({
                    ...payloadUploadRequest,
                    file: null
                }));
                if (!isUploadUrl) {
                    fileSelectorRef.current.value = ""
                }
            break

            case "file":
                let file = event.target.files[0]
                if (file) {
                    const isValidExtension = selectedFileFormat.some(ext => file.name.endsWith(ext));
                    if (!isValidExtension) {
                        event.target.value = '';
                        setPayloadUploadRequest(payloadUploadRequest => ({
                            ...payloadUploadRequest,
                            file: null
                        }));
                        return
                    }
                }
                let maxSize = 500 * 1024 * 1024;
                // let maxSize = Infinity;
                // if (payloadUploadRequest.fileFormat === "csv") {
                //   maxSize = 750 * 1024 * 1024;
                // } 
                // else if (payloadUploadRequest.fileFormat === "shapefile") {
                //   maxSize = 250 * 1024 * 1024;
                // } 
                setMaxFileSize(maxSize);
                if (file.size > maxSize) {
                    event.target.value = '';
                    setPayloadUploadRequest(prev => ({ ...prev, file: null }));
                    toast({
                        title: t('toastMsg.error'),
                        description: t('toastMsg.uploadDatasetModal.fileExceedsMaximumSize') + ' (MAX : ' + (maxSize / (1024 * 1024)) + ' MB)',
                        status: 'error',
                        duration: 4000,
                        isClosable: true
                    });
                    return;
                }

                if (hasAccents(file.name)) {
                    event.target.value = '';
                    setPayloadUploadRequest(payloadUploadRequest => ({
                        ...payloadUploadRequest,
                        file: null
                    }));

                    toast({
                        title: t('toastMsg.error'),
                        description: t('toastMsg.navbar.fileNameCannotContainAccents'),
                        status: 'warning',
                        duration: 4000,
                        isClosable: true
                    })
                    return
                }

                setPayloadUploadRequest(payloadUploadRequest => ({
                    ...payloadUploadRequest,
                    [key]: file
                }));

            break

            case "is_update":
                const { checked } = event.target;
                setIsUpdate(checked);
                setPayloadUploadRequest(payloadUploadRequest => ({
                    ...payloadUploadRequest,
                    [key]: checked
                }));
            break

            case "updateDate":
                setPayloadUploadRequest(payloadUploadRequest => ({
                    ...payloadUploadRequest,
                    [key]: event.target.value
                }));
            break;

            default:
            break
        }
    };

    return(
        <Modal onClose={() => { onClose(); setSelectedFileFormat(fileFormats[0].value); setPayloadUploadRequest(defaultPayloadUploadRequestValue) }} isOpen={isOpen} isCentered closeOnOverlayClick={false}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader color="white" display="flex" justifyContent="space-between" bgColor={"#0888BE"}><Box>{isUploadUrl ? t('uploadDatasetModal.upload') + " Dataset URL" : t('uploadDatasetModal.upload') + " Dataset"}</Box> <Box><CloseButton disabled={isUploading} onClick={() => { onClose(); setSelectedFileFormat(fileFormats[0].value); setPayloadUploadRequest(defaultPayloadUploadRequestValue); setIsUpdate(false) }} /></Box></ModalHeader>
                <ModalBody>
                    <Stack spacing='4'>
                        <Box>
                            <FormControl mb={4} isRequired>
                                <FormLabel fontSize='lg' color="rgba(11,78,90,255)" fontWeight='semibold'> Dataset {t('uploadDatasetModal.version')}</FormLabel>
                                <NumberInput
                                    min={1.0}
                                    step={0.1}
                                    precision={1}
                                    defaultValue={1.0}
                                    disabled={isUploading}
                                    value={payloadUploadRequest.version} // Collega il valore allo stato 
                                    onChange={(valueString) => handleChangePayloadRequest(valueString, 'version')}
                                >
                                    <NumberInputField />
                                    <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                    </NumberInputStepper>
                                </NumberInput>
                            </FormControl>
                            <FormControl display='flex' alignItems='center'>
                                <FormLabel htmlFor='email-alerts' mb='2'>
                                    {t('uploadDatasetModal.isTheFileAnUpdate')}
                                </FormLabel>
                                <Switch disabled={isUploading} id='email-alerts' onChange={(e) => handleChangePayloadRequest(e, "is_update")} />
                            </FormControl>
                            {isUpdate && (
                                <>
                                    <Box bg="yellow.100" p={3} borderRadius="md">
                                        <Text fontSize="sm" fontWeight="bold">
                                            {t('uploadDatasetModal.msgFileUpdate')}
                                        </Text>
                                    </Box>
                                    <FormControl mb={4} isRequired>
                                        <FormLabel fontSize='lg' color="rgba(11,78,90,255)" fontWeight='semibold'>{t('uploadDatasetModal.updateDate')}</FormLabel>
                                        <Input disabled={isUploading} type="date" required onChange={(e) => handleChangePayloadRequest(e, 'updateDate')} />
                                    </FormControl>
                                </>
                            )}
                            {!isUpdate && (
                                <FormControl isRequired mb={4}>
                                    <Box>
                                        <FormLabel fontSize='lg' color="rgba(11,78,90,255)" fontWeight='semibold'>{t('uploadDatasetModal.title')} :</FormLabel>
                                        <Input disabled={isUploading} placeholder='Titolo' onChange={(e) => handleChangePayloadRequest(e, "titolo")} />
                                    </Box>
                                </FormControl>
                            )}
                            <FormControl isRequired>
                                <FormLabel fontSize='lg' color="rgba(11,78,90,255)" fontWeight='semibold'>{t('uploadDatasetModal.fileFormat')}:</FormLabel>
                                <Select disabled={Boolean(payloadUploadRequest.urlDownloadFile) || isUploading} onChange={(e) => handleChangePayloadRequest(e, "fileFormat")} >
                                    {fileFormatsOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                        {option.label}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* <FormControl display='flex' alignItems='center'>
                            <FormLabel htmlFor='email-alerts' mb='0'>
                            {t('catalog.dataset.isTheFileAnUpdate')}
                            </FormLabel>
                            <Switch id='email-alerts' onChange={(e) => handleChangePayloadRequest(e, "is_update")} />
                        </FormControl> */}

                        {isUploadUrl && (
                            <Box>
                                <FormControl isRequired>
                                    <FormLabel fontSize='lg' color="rgba(11,78,90,255)" fontWeight='semibold' >{t('uploadDatasetModal.insertLinkDownloadDataset')}:</FormLabel>
                                    <InputGroup>
                                        <Input
                                            placeholder={t('uploadDatasetModal.insertUrl')}
                                            value={currentUrl}
                                            onChange={(e) => setCurrentUrl(e.target.value)}
                                            disabled={Boolean(payloadUploadRequest.urlDownloadFile)}
                                        />
                                        <InputRightElement width="4.5rem">
                                        <Button
                                            h="1.75rem"
                                            size="sm"
                                            colorScheme="teal"
                                            isLoading={isValidating}
                                            onClick={validateUrl}
                                            disabled={Boolean(payloadUploadRequest.urlDownloadFile)}
                                        >
                                            {t('uploadDatasetModal.add')}
                                        </Button>
                                        </InputRightElement>
                                    </InputGroup>
                                </FormControl>
                                {payloadUploadRequest.urlDownloadFile && (
                                    <Box mt={2} mb={4} maxW="100%">
                                        <HStack justify="space-between" w="100%">
                                            <HStack flex="1" minW="0">
                                                <Icon as={MdCheckCircle} color="green" boxSize="15px" />
                                                <Tooltip label={payloadUploadRequest.urlDownloadFile} placement="right" >
                                                    <Text fontSize="sm" isTruncated>
                                                        {payloadUploadRequest.urlDownloadFile}
                                                    </Text>
                                                </Tooltip>
                                            </HStack>
                                            <HStack>
                                                <Button
                                                    size="xs"
                                                    variant="ghost"
                                                    colorScheme="blue"
                                                    leftIcon={<FaDownload />}
                                                    onClick={() => window.open(payloadUploadRequest.urlDownloadFile, '_blank')}
                                                >
                                                    {t('uploadDatasetModal.download')}
                                                </Button>
                                                <Button
                                                    size="xs"
                                                    variant="ghost"
                                                    colorScheme="red"
                                                    leftIcon={<MdDelete />}
                                                    onClick={removeUrl}
                                                >
                                                    {t('uploadDatasetModal.remove')}
                                                </Button>
                                            </HStack>
                                        </HStack>
                                    </Box>
                                )}
                            </Box>
                        )}
                        {!isUploadUrl && (
                            <Box>
                                <FormControl isRequired>
                                    <FormLabel fontSize='lg' color="rgba(11,78,90,255)" fontWeight='semibold' >{t('uploadDatasetModal.selectFile')}:</FormLabel>
                                    <Input disabled={isUploading || (payloadUploadRequest.fileFormat == null || payloadUploadRequest.fileFormat === "")} ref={fileSelectorRef} placeholder='Select file' name='file' type='file' bg='white' accept={selectedFileFormat} onChange={(e) => handleChangePayloadRequest(e, "file")} />
                                </FormControl>
                            </Box>
                        )}
                    </Stack>
                    {isUploading && (
                        <Box position="relative" width="100%" maxW="500px" height="24px" borderRadius="md" overflow="hidden">
                            <Progress value={uploadProgress} size="sm" colorScheme="green" height="100%" borderRadius="inherit" />
                            <Text
                                position="absolute"
                                left={uploadProgress >= 100 ? 'calc(100% - 40px)' : `calc(${uploadProgress}% - 30px)`}
                                top="50%"
                                transform="translateY(-50%)"
                                fontSize="sm"
                                fontWeight="bold"
                                color="white"
                            >
                                {Math.round(uploadProgress)}%
                            </Text>
                        </Box>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button colorScheme='green' mr={3} onClick={uploadDataset} isLoading={isUploading}>
                        {t('uploadDatasetModal.submit')}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
export default UploadDatasetModal;